import { useMemo } from "react";
import { Disc, GridBoard, type GridCell } from "./GridBoard.js";
import { haptic } from "../lib/telegram.js";
import type { CellMove, GomokuView, ReversiView } from "../lib/views.js";

/**
 * Доска для игр «поставил фишку в клетку»: реверси и гомоку.
 *
 * Отличаются только тем, как красится клетка, поэтому обе обслуживает один
 * компонент. Следующая такая игра подключается новой веткой в `cellsOf`.
 */

export function ReversiBoard({
  view,
  onMove,
}: {
  view: ReversiView;
  onMove(move: CellMove): void;
}) {
  const myTurn = view.turn === view.me;
  const playable = useMemo(
    () => new Set(view.legalMoves.map((m) => m.cell)),
    [view.legalMoves],
  );

  const cells: GridCell[] = view.board.map((v, i) => ({
    dark: true,
    ...(v !== 0
      ? { content: <Disc color={v === 1 ? "black" : "white"} layoutId={`disc-${i}`} /> }
      : {}),
    playable: myTurn && playable.has(i),
    lastMove: view.lastMove === i,
    highlight: view.lastFlipped.includes(i),
  }));

  return (
    <div className="stack" style={{ gap: 8 }}>
      <GridBoard size={view.size} cells={cells} onCell={(i) => tap(i, playable, myTurn, onMove)} />
      <div className="row" style={{ justifyContent: "space-between" }}>
        <span className="row" style={{ gap: 6 }}>
          <span className={`disc disc-${view.myMark === 1 ? "black" : "white"} is-inline`} />
          <span className="small" style={{ fontWeight: 700 }}>{view.score.mine}</span>
        </span>
        <span className="row" style={{ gap: 6 }}>
          <span className="small muted">{view.score.theirs}</span>
          <span className={`disc disc-${view.myMark === 1 ? "white" : "black"} is-inline`} />
        </span>
      </div>
    </div>
  );
}

export function GomokuBoard({
  view,
  onMove,
}: {
  view: GomokuView;
  onMove(move: CellMove): void;
}) {
  const myTurn = view.turn === view.me;
  // Гомоку на 15×15: подсвечивать 200+ пустых клеток бессмысленно и шумно,
  // поэтому подсказку не рисуем — просто разрешаем тап по пустой.
  const playable = useMemo(
    () => new Set(view.legalMoves.map((m) => m.cell)),
    [view.legalMoves],
  );

  const cells: GridCell[] = view.board.map((v, i) => ({
    ...(v !== 0
      ? { content: <Disc color={v === 1 ? "black" : "white"} layoutId={`stone-${i}`} /> }
      : {}),
    lastMove: view.lastMove === i,
    highlight: view.winLine?.includes(i) ?? false,
  }));

  return (
    <GridBoard
      size={view.size}
      cells={cells}
      lined
      onCell={(i) => tap(i, playable, myTurn, onMove)}
    />
  );
}

function tap(
  index: number,
  playable: Set<number>,
  myTurn: boolean,
  onMove: (m: CellMove) => void,
): void {
  if (!myTurn || !playable.has(index)) return;
  haptic.move();
  onMove({ cell: index });
}
